import React, { useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  CountryProfileInfo,
  CountryProfileInfoType,
  ICountriesProfileProps,
  ITermsAndCondition,
  countryComboBoxStyles,
  textFieldGray,
} from "./CountriesProfile.type";
import {
  Card,
  CardFooterBase,
  CardStandardHeader,
} from "@coherence-design-system/controls";
import {
  Stack,
  IBreadcrumbItem,
  Breadcrumb,
  Label,
  FontSizes,
  FontWeights,
  StackItem,
  TextField,
  ComboBox,
  PrimaryButton,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import { BreadcrumbStyle } from "../Quotation/AddQuotation/AddQuotationStyle";
import { stackTokensWithGap10 } from "../Quotation/QuotationStyle";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import {
  CountryProfileContext,
  intialCountryProfileInfo,
} from "./CountriesProfileContext";
import {
  AUSTRIA,
  BELGIUM,
  CountriesData,
  allCountries,
  remittanceCurrencyCodeAndCompanyCodeCountries,
} from "./Common/CountriesData";
import {
  ABN_IS_REQUIRED,
  ACCOUNT_ADDRESS_REQUIRED,
  ACCOUNT_NAME_REQUIRED,
  ACCOUNT_NUMBER_REQUIRED,
  ADDRESS_REQUIRED,
  BANK_ADDRESS_REQUIRED,
  BANK_KEY_REQUIRED,
  BANK_NAME_REQUIRED,
  BANK_PHONE_REQUIRED,
  BC_CODE_REQUIRED,
  BSB_CODE_REQUIRED,
  CITY_REQUIRED,
  COMPANY_CODE_REQUIRED,
  CORRESPONDING_BANK_REQUIRED,
  CORRESPONDING_BANK_SWIFT_OR_BIC_REQUIRED,
  CURRENCY_CODE_REQUIRED,
  IBAN_NUMBER_REQUIRED,
  NAME_IS_REQUIRED,
  POSTALCODE_REQUIRED,
  SORT_CODE_REQUIRED,
  STREET_REQUIRED,
  SWIFT_BIC_CODE_REQUIRED,
  SWIFT_CODE_REQUIRED,
  SWITCH_CODE_REQUIRED,
  VAT_ID_REQUIRED,
  validateCountryProfileDetailsFields,
} from "./CountriesProfileValidation";
import { CountryProfileService } from "../../services/CountryProfiles/CountryProfileService";
import { countryWiseStateList } from "../CustomerAccount/CustomerFormInitialValues";
import { useLocalStorageContext } from "../../Provider/LocalStorageProvider";
import { UserRoles } from "../../common/enum/UserRoles";
import styles from "./CountryProfile.module.css";
import { DateFormatConfigCard } from "./components/DateFormatConfigCard";
import { getDefaultDateFormatForCountry } from "../../Utils/DateFormatUtility";
const CountryProfileEditDetails: React.FunctionComponent<
  ICountriesProfileProps
> = (props) => {
  const { userRole } = useLocalStorageContext();
  props.onCountryProfile(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { countryProfileInfo, setCountryProfileInfo } = useContext(
    CountryProfileContext
  ) as CountryProfileInfoType;
  useEffect(() => {
    if (location) {
      const countryInfo = location?.state as CountryProfileInfo;
      
      // Get default date format for the country if not already in database
      if (!countryInfo.dateFormat && countryInfo.countryName) {
        countryInfo.dateFormat = getDefaultDateFormatForCountry(countryInfo.countryName);
      }
      
      setCountryProfileInfo(countryInfo);
    }
  }, []);
  const [termsAndCondiditionSelectedFile, setTermsAndCondiditionSelectedFile] =
    useState<File>();
  const termsAndConditionFileInputRef = useRef<HTMLInputElement>(null);
  const handleTermsAndConditionFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTermsAndCondiditionSelectedFile(event.target.files?.[0]);
  };
  const handleTermsAndConditionClick = () => {
    if (termsAndConditionFileInputRef.current) {
      termsAndConditionFileInputRef.current.click();
    }
  };
  const [officeDetailsEdit, setOfficeDetailsEdit] = useState(false);
  const [remittanceAddressDetailsEdit, setRemittanceAddressDetailsEdit] =
    useState(false);
  const [dateFormatEdit, setDateFormatEdit] = useState(false);
  const [countryProfileError, setCountryProfileError] = useState(
    intialCountryProfileInfo
  );
  const [filteredOriginState, setFilteredOriginState] = useState<any[]>([]);
  const [filteredWarehouseState, setFilteredWarehouseState] = useState<any[]>(
    []
  );
  const SubmitButtonStyle = {
    marginTop: "20px",
    paddding: "10px",
    marginBottom: "20px",
  };
  const TextFieldStyles = {
    fieldGroup: {
      width: "290px",
    },
  };
  const appInsights = useAppInsightsContext();
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const statesOriginData = countryWiseStateList.filter((item) => {
      return (
        item.country.toUpperCase() ===
        countryProfileInfo.administrativeOriginCountry?.toUpperCase()
      );
    });
    setFilteredOriginState(statesOriginData);
    const statesWarehouseData = countryWiseStateList.filter((item) => {
      return (
        item.country.toUpperCase() ===
        countryProfileInfo.warehouseCountry?.toUpperCase()
      );
    });
    setFilteredWarehouseState(statesWarehouseData);
  }, [officeDetailsEdit, remittanceAddressDetailsEdit, dateFormatEdit]);
  const getBreadcrumbItems = () => {
    const items: IBreadcrumbItem[] = [
      { text: "Countries", key: "Country", href: "/#CountryProfile" },
      {
        text: `${countryProfileInfo.countryName}`,
        key: `${countryProfileInfo.countryName}`,
      },
    ];
    return items;
  };
  const pBoldStyle = {
    fontWeight: FontWeights.bold,
    padding: "5px",
  };
  const _OnClickSubmitButton = async (e: Event) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let { isValidated, countryProfileErrors } =
        validateCountryProfileDetailsFields(countryProfileInfo);
      if (isValidated) {
        let countryInfo = CountriesData.filter(
          (x) =>
            x.countryName.toLowerCase() ===
            countryProfileInfo.countryName.toLowerCase()
        )[0];
        const remittanceCurrency = remittanceCurrencyCodeAndCompanyCodeCountries.includes(countryProfileInfo?.countryName.toLowerCase());
        const remittanceCompany = (remittanceCurrency) || [AUSTRIA.toLowerCase(), BELGIUM.toLowerCase()].includes(countryProfileInfo?.countryName?.toLowerCase());
        let updatedCountryProfile = {
          countryName: countryProfileInfo?.countryName,
          twoDigitCountryCode:
            countryProfileInfo?.twoDigitCountryCode?.length > 0
              ? countryProfileInfo.twoDigitCountryCode
              : countryInfo?.twoDigitCountryCode,
          currencyCode:
            countryProfileInfo?.currencyCode?.length > 0
              ? countryProfileInfo.currencyCode
              : countryInfo?.currencyCode,
          countryDigitCode:
            countryProfileInfo?.countryDigitCode?.length > 0
              ? countryProfileInfo.countryDigitCode
              : null,
          languageCode:
            countryProfileInfo?.languageCode?.length > 0
              ? countryProfileInfo.languageCode
              : countryInfo?.languageCode,
          region:
            countryProfileInfo?.region?.length > 0
              ? countryProfileInfo.region
              : countryInfo?.region,
          countryCode:
            countryProfileInfo?.countryCode?.length > 0
              ? countryProfileInfo.countryCode
              : countryInfo?.countryCode,
          warehouseCountry:
            countryProfileInfo?.warehouseCountry?.length > 0
              ? countryProfileInfo.warehouseCountry
              : null,
          warehouseCity:
            countryProfileInfo?.warehouseCity?.length > 0
              ? countryProfileInfo.warehouseCity
              : null,
          warehouseStreet:
            countryProfileInfo?.warehouseStreet?.length > 0
              ? countryProfileInfo.warehouseStreet
              : null,
          warehouseZipCode:
            countryProfileInfo?.warehouseZipCode?.length > 0
              ? countryProfileInfo.warehouseZipCode
              : null,
          warehouseState:
            countryProfileInfo?.warehouseState?.length > 0
              ? countryProfileInfo.warehouseState
              : null,
          administrativeOriginName:
            countryProfileInfo?.administrativeOriginName?.length > 0
              ? countryProfileInfo?.administrativeOriginName
              : null,
          administrativeOriginState:
            countryProfileInfo?.administrativeOriginState?.length > 0
              ? countryProfileInfo.administrativeOriginState
              : null,
          administrativeOriginCountry:
            countryProfileInfo?.administrativeOriginCountry?.length > 0
              ? countryProfileInfo.administrativeOriginCountry
              : null,
          administrativeOriginCity:
            countryProfileInfo?.administrativeOriginCity?.length > 0
              ? countryProfileInfo.administrativeOriginCity
              : null,
          administrativeOriginStreet:
            countryProfileInfo?.administrativeOriginStreet?.length > 0
              ? countryProfileInfo.administrativeOriginStreet
              : null,
          administrativeOriginZipCode:
            countryProfileInfo?.administrativeOriginZipCode?.length > 0
              ? countryProfileInfo.administrativeOriginZipCode
              : null,
          eTag:
            countryProfileInfo?.eTag?.length > 0
              ? countryProfileInfo.eTag
              : null,
          id: countryProfileInfo?.id?.length > 0 ? countryProfileInfo.id : null,
          partitionKey:
            countryProfileInfo?.partitionKey?.length > 0
              ? countryProfileInfo.partitionKey
              : null,
          companyCode:
            countryProfileInfo?.companyCode?.length > 0
              ? countryProfileInfo.companyCode
              : countryProfileInfo?.companyCode,
          isActive:
            countryProfileInfo?.isActive?.length > 0
              ? countryProfileInfo.isActive
              : null,
          createdBy:
            countryProfileInfo?.createdBy?.length > 0
              ? countryProfileInfo.createdBy
              : null,
          updatedBy:
            countryProfileInfo?.updatedBy?.length > 0
              ? countryProfileInfo.updatedBy
              : null,
          createdAt:
            countryProfileInfo?.createdAt?.length > 0
              ? countryProfileInfo.createdAt
              : null,
          updatedAt:
            countryProfileInfo?.updatedAt?.length > 0
              ? countryProfileInfo.updatedAt
              : null,
          vatId:
            countryProfileInfo?.vatId?.length > 0
              ? countryProfileInfo.vatId
              : null,
          termsAndConditions:
            countryProfileInfo?.termsAndConditions?.length > 0
              ? countryProfileInfo.termsAndConditions
              : [],
          bankName:
            countryProfileInfo?.bankName?.length > 0
              ? countryProfileInfo.bankName
              : null,
          bankAddress:
            countryProfileInfo?.bankAddress?.length > 0
              ? countryProfileInfo.bankAddress
              : null,
          addressLine1: countryProfileInfo?.addressLine1?.length > 0 ? countryProfileInfo?.addressLine1 : null,
          addressLine2: countryProfileInfo?.addressLine2?.length > 0 ? countryProfileInfo?.addressLine2 : null,
          addressLine3: countryProfileInfo?.addressLine3?.length > 0 ? countryProfileInfo?.addressLine3 : null,
          accountName:
            countryProfileInfo?.accountName?.length > 0
              ? countryProfileInfo.accountName
              : null,
          accountNumber:
            countryProfileInfo?.accountNumber?.length > 0
              ? countryProfileInfo.accountNumber
              : null,
          accountAddress:
            countryProfileInfo?.accountAddress?.length > 0
              ? countryProfileInfo.accountAddress
              : null,
          iban:
            countryProfileInfo?.iban?.length > 0
              ? countryProfileInfo.iban
              : null,
          swiftCode:
            countryProfileInfo?.swiftCode?.length > 0
              ? countryProfileInfo.swiftCode
              : null,
          swiftBICCode:
            countryProfileInfo?.swiftBicCode?.length > 0
              ? countryProfileInfo.swiftBicCode
              : null,
          switchCode:
            countryProfileInfo?.switchCode?.length > 0
              ? countryProfileInfo.switchCode
              : null,
          correspondingBank:
            countryProfileInfo?.correspondingBank?.length > 0
              ? countryProfileInfo.correspondingBank
              : null,
          correspondingBankSwiftBICCode:
            countryProfileInfo?.correspondingBankSwiftBICCode?.length > 0
              ? countryProfileInfo.correspondingBankSwiftBICCode
              : null,
          bankCompanyCode:
            countryProfileInfo?.bankCompanyCode?.length > 0
              ? countryProfileInfo.bankCompanyCode
              : null,
          sortCode:
            countryProfileInfo?.sortCode?.length > 0
              ? countryProfileInfo.sortCode
              : null,
          currency:
            countryProfileInfo?.currencyCode?.length > 0
              ? countryProfileInfo.currencyCode
              : countryInfo?.currencyCode,
          bcCode:
            countryProfileInfo?.bcCode?.length > 0
              ? countryProfileInfo.bcCode
              : null,
          abn:
            countryProfileInfo?.abn?.length > 0 ? countryProfileInfo.abn : null,
          bsbCode:
            countryProfileInfo?.bsbCode?.length > 0
              ? countryProfileInfo.bsbCode
              : null,
          bankKey:
            countryProfileInfo?.bankKey?.length > 0
              ? countryProfileInfo.bankKey
              : null,
          bankPhone:
            countryProfileInfo?.bankPhone?.length > 0
              ? countryProfileInfo.bankPhone
              : null,
          remittanceCurrencyCode: (remittanceCurrency) ? countryProfileInfo?.currencyCode : "",
          remittanceCompanyCode: (remittanceCompany) ? countryProfileInfo?.companyCode : "",
          dateFormat: countryProfileInfo?.dateFormat || 
                   localStorage.getItem('selectedDateFormat') || 
                   localStorage.getItem('dateFormat') || 
                   getDefaultDateFormatForCountry(countryProfileInfo?.countryName),
        };
       const promises = [];
        if (termsAndCondiditionSelectedFile) {
          const termsAndConditionFromData = new FormData();
          termsAndConditionFromData.append(
            "file",
            termsAndCondiditionSelectedFile
          );
          const documentId = uuidv4();
          const temrsAndConditionDoucmentPromise =
            await CountryProfileService.uploadDocument(
              documentId,
              termsAndConditionFromData
            )
              .then((res: any) => {
                if (res !== undefined) {
                  const tAndCFile: ITermsAndCondition = {
                    id: documentId,
                    name: termsAndCondiditionSelectedFile?.name,
                    createdAt: `${new Date().toLocaleDateString() +
                      " " +
                      new Date().toLocaleTimeString()
                      }`,
                  };
                  updatedCountryProfile.termsAndConditions.push(tAndCFile);
                }
              })
              .catch((error) => {
                setIsLoading(false);
                appInsights.trackException({
                  error: new Error(error as string),
                  properties: {
                    name: "Error in uploadDocument API in  CountryProfileEditDetails.tsx",
                  },
                });
              });
          promises.push(temrsAndConditionDoucmentPromise);
        }
        Promise.all(promises);
        const countryProfilePromise =
          await CountryProfileService.updateCountryProfile(
            updatedCountryProfile
          )
            .then((res: any) => {
              // Reset edit mode states
              setOfficeDetailsEdit(false);
              setRemittanceAddressDetailsEdit(false);
              setDateFormatEdit(false);
              navigate("/CountryProfile");
            })
            .catch((error) => {
              setIsLoading(false);
              appInsights.trackException({
                error: new Error(error as string),
                properties: {
                  name: "Error while updating countryProfile in  CountryProfileEditDetails.tsx",
                },
              });
            });
      } else {
        setIsLoading(false);
        setCountryProfileError(countryProfileErrors);
      }
    } catch (error) {
      setIsLoading(false);
      appInsights.trackException({
        error: new Error(error as string),
        properties: {
          name: "Error in updateCountryProfile API in CountryProfileEditDetails.tsx",
        },
      });
    }
  };
  const _OnClickHandleDownloadFile = async (index: number) => {
    try {
      const response = await CountryProfileService.getDocumentBlob(
        countryProfileInfo?.termsAndConditions[index].id
      );
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute(
        "download",
        `${countryProfileInfo?.termsAndConditions[index].name}`
      );
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    } catch (error) {
      appInsights.trackException({
        error: new Error(error as string),
        properties: {
          name: "Error in getDocumentBlob API in CountryProfileEditDetails.tsx",
        },
      });
    }
  };
  const _OnClickActiveOrInactive = async (e: any) => {
    setIsLoading(true);
    const status = countryProfileInfo?.isActive == "true" ? "false" : "true";
    try {
      const activationStatus =
        await CountryProfileService.updateActivationStatus(
          countryProfileInfo?.id,
          countryProfileInfo?.partitionKey,
          status
        );
      if (activationStatus.status == 200) {
        setIsLoading(false);
        navigate("/CountryProfile");
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      appInsights.trackException({
        error: new Error(error as string),
        properties: {
          name: "Error while updating activation status",
        },
      });
    }
  }
  const countryRemittanceDetails = (countryName: string) => {
    switch (countryName.toLowerCase()) {
      case "ireland":
        return irelandRemittanceDetails();
      case "luxembourg":
        return luxembourgRemittanceDetails();
      case "portugal":
        return portugalRemittanceDetails();
      case "germany":
        return germanyRemittanceDetails();
      case "france":
        return franceRemittanceDetails();
      case "sweden":
        return swedanRemittanceDetails();
      case "norway":
        return norwayRemittanceDetails();
      case "denmark":
        return denmarkRemittanceDetails();
      case "switzerland":
        return switzerlandRemittanceDetails();
      case "belgium":
        return belgiumRemittanceDetails();
      case "austria":
        return austriaRemittanceDetails();
      case "finland":
        return finlandRemittanceDetails();
      case "netherlands":
        return netherlandsRemittanceDetails();
      case "italy":
        return italyRemittanceDetails();
      case "spain":
        return spainRemittanceDetails();
      case "japan":
        return japanRemittanceDetails();
      case "new zealand":
        return newzealandRemittanceDetails();
      case "singapore":
        return singaporeRemittanceDetails();
      case "australia":
        return australiaRemittanceDetails();
    }
  };
  const australiaRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNamValue) => {
                    if (bankNamValue && bankNamValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNamValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Address*"
                  name="bankAddress"
                  value={countryProfileInfo.bankAddress}
                  onChange={(e, bankAddressValue) => {
                    if (bankAddressValue && bankAddressValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankAddress: bankAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankAddress}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Swift Code*"
                  name="swiftCode"
                  value={countryProfileInfo.swiftCode}
                  onChange={(e, swiftCodeValue) => {
                    if (swiftCodeValue && swiftCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: SWIFT_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      swiftCode: swiftCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.swiftCode}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Australian Business Number (ABN)*​"
                  name="abn"
                  value={countryProfileInfo.abn}
                  onChange={(e, abnvalue) => {
                    if (abnvalue && abnvalue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        abn: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        abn: ABN_IS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      abn: abnvalue || "",
                    });
                  }}
                  errorMessage={countryProfileError.abn}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="BSB Code*​"
                  name="bsbCode"
                  value={countryProfileInfo.bsbCode}
                  onChange={(e, bsbCodeValue) => {
                    if (bsbCodeValue && bsbCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bsbCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bsbCode: BSB_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bsbCode: bsbCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bsbCode}
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  const irelandRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNamValue) => {
                    if (bankNamValue && bankNamValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNamValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Address*"
                  name="bankAddress"
                  value={countryProfileInfo.bankAddress}
                  onChange={(e, bankAddressValue) => {
                    if (bankAddressValue && bankAddressValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankAddress: bankAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankAddress}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="IBAN Number*"
                  name="ibanNumber"
                  value={countryProfileInfo.iban}
                  onChange={(e, ibanValue) => {
                    if (ibanValue && ibanValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: IBAN_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      iban: ibanValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.iban}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Swift Code*"
                  name="swiftCode"
                  value={countryProfileInfo.swiftCode}
                  onChange={(e, swiftCodeValue) => {
                    if (swiftCodeValue && swiftCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: SWIFT_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      swiftCode: swiftCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.swiftCode}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="VAT*"
                  name="vatId"
                  value={countryProfileInfo.vatId}
                  onChange={(e, vatIdValue) => {
                    if (vatIdValue && vatIdValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: VAT_ID_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      vatId: vatIdValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.vatId}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Sort Code*"
                  name="sortCode"
                  value={countryProfileInfo.sortCode}
                  onChange={(e, sortCodeValue) => {
                    if (sortCodeValue && sortCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        sortCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: SORT_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      sortCode: sortCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.sortCode}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Corresponding Bank*"
                  name="correspondingBank"
                  value={countryProfileInfo.correspondingBank}
                  onChange={(e, correspondingBankValue) => {
                    if (
                      correspondingBankValue &&
                      correspondingBankValue?.length > 0
                    ) {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBank: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBank: CORRESPONDING_BANK_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      correspondingBank: correspondingBankValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.correspondingBank}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Corresponding Bank Swift / BIC Code*"
                  name="correspondingBankSwiftBICCode"
                  value={countryProfileInfo.correspondingBankSwiftBICCode}
                  onChange={(e, correspondingBankSwiftBICCodeValue) => {
                    if (
                      correspondingBankSwiftBICCodeValue &&
                      correspondingBankSwiftBICCodeValue?.length > 0
                    ) {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBankSwiftBICCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBankSwiftBICCode:
                          CORRESPONDING_BANK_SWIFT_OR_BIC_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      correspondingBankSwiftBICCode:
                        correspondingBankSwiftBICCodeValue || "",
                    });
                  }}
                  errorMessage={
                    countryProfileError.correspondingBankSwiftBICCode
                  }
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  const luxembourgRemittanceDetails = irelandRemittanceDetails;
  const portugalRemittanceDetails = irelandRemittanceDetails;
  const germanyRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNameValue) => {
                    if (bankNameValue && bankNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Address*"
                  name="bankAddress"
                  value={countryProfileInfo.bankAddress}
                  onChange={(e, bankAddressValue) => {
                    if (bankAddressValue && bankAddressValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankAddress: bankAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankAddress}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Phone*"
                  name="bankPhone"
                  value={countryProfileInfo.bankPhone}
                  onChange={(e, bankPohneValue) => {
                    if (bankPohneValue && bankPohneValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankPhone: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_PHONE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankPhone: bankPohneValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankPhone}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Address*"
                  name="accountAddress"
                  value={countryProfileInfo.accountAddress}
                  onChange={(e, accountAddressValue) => {
                    if (
                      accountAddressValue &&
                      accountAddressValue?.length > 0
                    ) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountAddress: ACCOUNT_ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountAddress: accountAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountAddress}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="IBAN Number*"
                  name="ibanNumber"
                  value={countryProfileInfo.iban}
                  onChange={(e, ibanValue) => {
                    if (ibanValue && ibanValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: IBAN_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      iban: ibanValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.iban}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Swift Code*"
                  name="swiftCode"
                  value={countryProfileInfo.swiftCode}
                  onChange={(e, swiftCodeValue) => {
                    if (swiftCodeValue && swiftCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: SWIFT_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      swiftCode: swiftCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.swiftCode}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="VAT*"
                  name="vatId"
                  value={countryProfileInfo.vatId}
                  onChange={(e, vatIdValue) => {
                    if (vatIdValue && vatIdValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: VAT_ID_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      vatId: vatIdValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.vatId}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Corresponding Bank Swift / BIC Code*"
                  name="correspondingBankSwiftBICCode"
                  value={countryProfileInfo.correspondingBankSwiftBICCode}
                  onChange={(e, correspondingBankSwiftBICCodeValue) => {
                    if (
                      correspondingBankSwiftBICCodeValue &&
                      correspondingBankSwiftBICCodeValue?.length > 0
                    ) {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBankSwiftBICCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBankSwiftBICCode:
                          CORRESPONDING_BANK_SWIFT_OR_BIC_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      correspondingBankSwiftBICCode:
                        correspondingBankSwiftBICCodeValue || "",
                    });
                  }}
                  errorMessage={
                    countryProfileError.correspondingBankSwiftBICCode
                  }
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  const franceRemittanceDetails = germanyRemittanceDetails;
  const swedanRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNameValue) => {
                    if (bankNameValue && bankNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Address*"
                  name="bankAddress"
                  value={countryProfileInfo.bankAddress}
                  onChange={(e, bankAddressValue) => {
                    if (bankAddressValue && bankAddressValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankAddress: bankAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankAddress}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="IBAN Number*"
                  name="ibanNumber"
                  value={countryProfileInfo.iban}
                  onChange={(e, ibanValue) => {
                    if (ibanValue && ibanValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: IBAN_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      iban: ibanValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.iban}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Swift Code*"
                  name="swiftCode"
                  value={countryProfileInfo.swiftCode}
                  onChange={(e, swiftCodeValue) => {
                    if (swiftCodeValue && swiftCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: SWIFT_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      swiftCode: swiftCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.swiftCode}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="VAT*"
                  name="vatId"
                  value={countryProfileInfo.vatId}
                  onChange={(e, vatIdValue) => {
                    if (vatIdValue && vatIdValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: VAT_ID_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      vatId: vatIdValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.vatId}
                />
                <TextField
                  styles={textFieldGray}
                  label="Currency Code*"
                  name="currencyCode"
                  value={countryProfileInfo.currencyCode}
                  onChange={(e, currencyCodeValue) => {
                    if (currencyCodeValue && currencyCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        currencyCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        currencyCode: CURRENCY_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      currencyCode: currencyCodeValue || "",
                    });
                  }}
                  readOnly
                  errorMessage={countryProfileError.currencyCode}
                />
                <TextField
                  styles={textFieldGray}
                  label="Company Code*"
                  name="companyCode"
                  value={countryProfileInfo.companyCode}
                  onChange={(e, companyCodeValue) => {
                    if (companyCodeValue && companyCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        companyCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        companyCode: COMPANY_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      companyCode: companyCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.companyCode}
                  readOnly
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  const norwayRemittanceDetails = swedanRemittanceDetails;
  const denmarkRemittanceDetails = swedanRemittanceDetails;
  const switzerlandRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNamValue) => {
                    if (bankNamValue && bankNamValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNamValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Address*"
                  name="bankAddress"
                  value={countryProfileInfo.bankAddress}
                  onChange={(e, bankAddressValue) => {
                    if (bankAddressValue && bankAddressValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankAddress: bankAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankAddress}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="IBAN Number*"
                  name="ibanNumber"
                  value={countryProfileInfo.iban}
                  onChange={(e, ibanValue) => {
                    if (ibanValue && ibanValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: IBAN_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      iban: ibanValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.iban}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Swift Code*"
                  name="swiftCode"
                  value={countryProfileInfo.swiftCode}
                  onChange={(e, swiftCodeValue) => {
                    if (swiftCodeValue && swiftCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: SWIFT_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      swiftCode: swiftCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.swiftCode}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="VAT*"
                  name="vatId"
                  value={countryProfileInfo.vatId}
                  onChange={(e, vatIdValue) => {
                    if (vatIdValue && vatIdValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: VAT_ID_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      vatId: vatIdValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.vatId}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="BC Code*"
                  name="bcCode"
                  value={countryProfileInfo.bcCode}
                  onChange={(e, bcCodeValue) => {
                    if (bcCodeValue && bcCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bcCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bcCode: BC_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bcCode: bcCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bcCode}
                />
                <TextField
                  styles={textFieldGray}
                  label="Currency Code*"
                  name="currencyCode"
                  value={countryProfileInfo.currencyCode}
                  onChange={(e, currencyCodeValue) => {
                    if (currencyCodeValue && currencyCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        currencyCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        currencyCode: CURRENCY_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      currencyCode: currencyCodeValue || "",
                    });
                  }}
                  readOnly
                  errorMessage={countryProfileError.currencyCode}
                />
                <TextField
                  styles={textFieldGray}
                  label="Company Code*"
                  name="companyCode"
                  value={countryProfileInfo.companyCode}
                  onChange={(e, companyCodeValue) => {
                    if (companyCodeValue && companyCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        companyCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        companyCode: COMPANY_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      companyCode: companyCodeValue || "",
                    });
                  }}
                  readOnly
                  errorMessage={countryProfileError.companyCode}
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  const belgiumRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNamValue) => {
                    if (bankNamValue && bankNamValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNamValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Address*"
                  name="bankAddress"
                  value={countryProfileInfo.bankAddress}
                  onChange={(e, bankAddressValue) => {
                    if (bankAddressValue && bankAddressValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankAddress: bankAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankAddress}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="IBAN Number*"
                  name="ibanNumber"
                  value={countryProfileInfo.iban}
                  onChange={(e, ibanValue) => {
                    if (ibanValue && ibanValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: IBAN_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      iban: ibanValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.iban}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Swift Code*"
                  name="swiftCode"
                  value={countryProfileInfo.swiftCode}
                  onChange={(e, swiftCodeValue) => {
                    if (swiftCodeValue && swiftCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: SWIFT_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      swiftCode: swiftCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.swiftCode}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="VAT*"
                  name="vatId"
                  value={countryProfileInfo.vatId}
                  onChange={(e, vatIdValue) => {
                    if (vatIdValue && vatIdValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: VAT_ID_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      vatId: vatIdValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.vatId}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Corresponding Bank*"
                  name="correspondingBank"
                  value={countryProfileInfo.correspondingBank}
                  onChange={(e, correspondingBankValue) => {
                    if (
                      correspondingBankValue &&
                      correspondingBankValue?.length > 0
                    ) {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBank: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBank: CORRESPONDING_BANK_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      correspondingBank: correspondingBankValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.correspondingBank}
                />
                <TextField
                  styles={textFieldGray}
                  label="Company Code*"
                  name="companyCode"
                  value={countryProfileInfo.companyCode}
                  onChange={(e, companyCodeValue) => {
                    if (companyCodeValue && companyCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        companyCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        companyCode: COMPANY_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      companyCode: companyCodeValue || "",
                    });
                  }}
                  readOnly
                  errorMessage={countryProfileError.companyCode}
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  const austriaRemittanceDetails = belgiumRemittanceDetails;
  const finlandRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNamValue) => {
                    if (bankNamValue && bankNamValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNamValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Address*"
                  name="bankAddress"
                  value={countryProfileInfo.bankAddress}
                  onChange={(e, bankAddressValue) => {
                    if (bankAddressValue && bankAddressValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankAddress: bankAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankAddress}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="IBAN Number*"
                  name="ibanNumber"
                  value={countryProfileInfo.iban}
                  onChange={(e, ibanValue) => {
                    if (ibanValue && ibanValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        iban: IBAN_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      iban: ibanValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.iban}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Swift Code*"
                  name="swiftCode"
                  value={countryProfileInfo.swiftCode}
                  onChange={(e, swiftCodeValue) => {
                    if (swiftCodeValue && swiftCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: SWIFT_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      swiftCode: swiftCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.swiftCode}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="VAT*"
                  name="vatId"
                  value={countryProfileInfo.vatId}
                  onChange={(e, vatIdValue) => {
                    if (vatIdValue && vatIdValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        vatId: VAT_ID_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      vatId: vatIdValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.vatId}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Corresponding Bank*"
                  name="correspondingBank"
                  value={countryProfileInfo.correspondingBank}
                  onChange={(e, correspondingBankValue) => {
                    if (
                      correspondingBankValue &&
                      correspondingBankValue?.length > 0
                    ) {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBank: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        correspondingBank: CORRESPONDING_BANK_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      correspondingBank: correspondingBankValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.correspondingBank}
                />
                <TextField
                  styles={textFieldGray}
                  label="Currency Code*"
                  name="currencyCode"
                  value={countryProfileInfo.currencyCode}
                  onChange={(e, currencyCodeValue) => {
                    if (currencyCodeValue && currencyCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        currencyCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        currencyCode: CURRENCY_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      currencyCode: currencyCodeValue || "",
                    });
                  }}
                  readOnly
                  errorMessage={countryProfileError.currencyCode}
                />
                <TextField
                  styles={textFieldGray}
                  label="Company Code*"
                  name="companyCode"
                  value={countryProfileInfo.companyCode}
                  onChange={(e, companyCodeValue) => {
                    if (companyCodeValue && companyCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        companyCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        companyCode: COMPANY_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      companyCode: companyCodeValue || "",
                    });
                  }}
                  readOnly
                  errorMessage={countryProfileError.companyCode}
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  const netherlandsRemittanceDetails = finlandRemittanceDetails;
  const italyRemittanceDetails = finlandRemittanceDetails;
  const spainRemittanceDetails = finlandRemittanceDetails;
  const japanRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNamValue) => {
                    if (bankNamValue && bankNamValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNamValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Address Line 1*"
                  name="addressLine1"
                  value={countryProfileInfo.addressLine1}
                  onChange={(e, addressLine1) => {
                    if (addressLine1 && addressLine1?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        addressLine1: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        addressLine1: ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      addressLine1: addressLine1 || "",
                    });
                  }}
                  errorMessage={countryProfileError.addressLine1}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Address Line 2*"
                  name="bankAddress2"
                  value={countryProfileInfo.addressLine2}
                  onChange={(e, addressLine2) => {
                    if (
                      addressLine2 &&
                      addressLine2?.length > 0
                    ) {
                      setCountryProfileError({
                        ...countryProfileError,
                        addressLine2: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        addressLine2: ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      addressLine2: addressLine2 || "",
                    });
                  }}
                  errorMessage={countryProfileError.addressLine2}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Address Line 3*"
                  name="bankAddressLine2"
                  value={countryProfileInfo.addressLine3}
                  onChange={(e, addressLine3) => {
                    if (
                      addressLine3 &&
                      addressLine3?.length > 0
                    ) {
                      setCountryProfileError({
                        ...countryProfileError,
                        addressLine3: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        addressLine3: ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      addressLine3: addressLine3 || "",
                    });
                  }}
                  errorMessage={countryProfileError.addressLine3}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Swift/BIC Code*"
                  name="swiftBicCode"
                  value={countryProfileInfo.swiftBicCode}
                  onChange={(e, swiftBicCode) => {
                    if (swiftBicCode && swiftBicCode?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftBicCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftBicCode: SWIFT_BIC_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      swiftBicCode: swiftBicCode || "",
                    });
                  }}
                  errorMessage={countryProfileError.swiftBicCode}
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  const newzealandRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNamValue) => {
                    if (bankNamValue && bankNamValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNamValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Address*"
                  name="bankAddress"
                  value={countryProfileInfo.bankAddress}
                  onChange={(e, bankAddressValue) => {
                    if (bankAddressValue && bankAddressValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankAddress: bankAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankAddress}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Swift Code*"
                  name="swiftCode"
                  value={countryProfileInfo.swiftCode}
                  onChange={(e, swiftCodeValue) => {
                    if (swiftCodeValue && swiftCodeValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        swiftCode: SWIFT_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      swiftCode: swiftCodeValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.swiftCode}
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  const singaporeRemittanceDetails = () => {
    return (
      <Stack horizontal style={{ paddingLeft: 20, width: 658, paddingTop: 10 }}>
        <Card
          header={<CardStandardHeader cardTitle="Remittance Address Details" />}
        >
          <Stack horizontal horizontalAlign="space-between">
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Name*"
                  name="bankName"
                  value={countryProfileInfo.bankName}
                  onChange={(e, bankNamValue) => {
                    if (bankNamValue && bankNamValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankName: BANK_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankName: bankNamValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankName}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Address*"
                  name="bankAddress"
                  value={countryProfileInfo.bankAddress}
                  onChange={(e, bankAddressValue) => {
                    if (bankAddressValue && bankAddressValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankAddress: BANK_ADDRESS_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankAddress: bankAddressValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.bankAddress}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Account Name*"
                  name="accountName"
                  value={countryProfileInfo.accountName}
                  onChange={(e, accountNameValue) => {
                    if (accountNameValue && accountNameValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountName: ACCOUNT_NAME_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountName: accountNameValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountName}
                />
              </StackItem>
            </Stack>
            <Stack verticalFill verticalAlign="start">
              <StackItem verticalFill>
                <TextField
                  styles={TextFieldStyles}
                  label="Account Number*"
                  name="accountNumber"
                  value={countryProfileInfo.accountNumber}
                  onChange={(e, accountNumberValue) => {
                    if (accountNumberValue && accountNumberValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        accountNumber: ACCOUNT_NUMBER_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      accountNumber: accountNumberValue || "",
                    });
                  }}
                  errorMessage={countryProfileError.accountNumber}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Switch Code*"
                  name="swiitchCode"
                  value={countryProfileInfo.switchCode}
                  onChange={(e, switchCode) => {
                    if (switchCode && switchCode?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        switchCode: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        switchCode: SWITCH_CODE_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      switchCode: switchCode || "",
                    });
                  }}
                  errorMessage={countryProfileError.switchCode}
                />
                <TextField
                  styles={TextFieldStyles}
                  label="Bank Key*"
                  name="bankKey"
                  value={countryProfileInfo.bankKey}
                  onChange={(e, bankKeyValue) => {
                    if (bankKeyValue && bankKeyValue?.length > 0) {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankKey: "",
                      });
                    } else {
                      setCountryProfileError({
                        ...countryProfileError,
                        bankKey: BANK_KEY_REQUIRED,
                      });
                    }
                    setCountryProfileInfo({
                      ...countryProfileInfo,
                      bankKey: bankKeyValue || "",
                    });
                  }}
                  errorMessage={
                    countryProfileError.correspondingBankSwiftBICCode
                  }
                />
              </StackItem>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  };
  if (isLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }
  return (
    <Stack
      verticalFill
      verticalAlign="start"
      tokens={stackTokensWithGap10}
      style={{ backgroundColor: "#FAF9F8" }}
    >
      <Breadcrumb
        items={getBreadcrumbItems()}
        style={{ marginLeft: "1%", marginBottom: "-5px" }}
        styles={BreadcrumbStyle}
      />
      <Stack horizontal tokens={{ childrenGap: 50 }}>
        <Card>
          <Label
            style={{
              fontSize: FontSizes.size24,
              display: "inline",
              fontWeight: FontWeights.bold,
            }}
          >
            {`${countryProfileInfo.countryName}`}
          </Label>
        </Card>
      </Stack>
      {officeDetailsEdit ? (
        <Stack>
          <Stack
            horizontal
            style={{ paddingLeft: 20, width: 656, paddingTop: 10 }}
          >
            <Card
              header={
                <CardStandardHeader cardTitle="Microsoft Office Address" />
              }
            >
              <Stack horizontal horizontalAlign="space-between">
                <Stack verticalFill verticalAlign="start">
                  <StackItem verticalFill>
                    <TextField
                      styles={TextFieldStyles}
                      label="Name*"
                      name="name"
                      value={countryProfileInfo?.administrativeOriginName}
                      onChange={(e, administrativeOriginNameValue) => {
                        if (
                          administrativeOriginNameValue &&
                          administrativeOriginNameValue?.length > 0
                        ) {
                          setCountryProfileError({
                            ...countryProfileError,
                            administrativeOriginName: "",
                          });
                        } else {
                          setCountryProfileError({
                            ...countryProfileError,
                            administrativeOriginName: NAME_IS_REQUIRED,
                          });
                        }
                        setCountryProfileInfo({
                          ...countryProfileInfo,
                          administrativeOriginName:
                            administrativeOriginNameValue || "",
                        });
                      }}
                      errorMessage={
                        countryProfileError.administrativeOriginName
                      }
                    />
                    <TextField
                      styles={TextFieldStyles}
                      label="City*"
                      name="city"
                      value={countryProfileInfo.administrativeOriginCity}
                      onChange={(e, administrativeOrigincityValue) => {
                        if (
                          administrativeOrigincityValue &&
                          administrativeOrigincityValue?.length > 0
                        ) {
                          setCountryProfileError({
                            ...countryProfileError,
                            administrativeOriginCity: "",
                          });
                        } else {
                          setCountryProfileError({
                            ...countryProfileError,
                            administrativeOriginCity: CITY_REQUIRED,
                          });
                        }
                        setCountryProfileInfo({
                          ...countryProfileInfo,
                          administrativeOriginCity:
                            administrativeOrigincityValue || "",
                        });
                      }}
                      errorMessage={
                        countryProfileError.administrativeOriginCity
                      }
                    />
                    <TextField
                      styles={TextFieldStyles}
                      label="Zip/Postal Code*"
                      name="postalCode"
                      value={countryProfileInfo.administrativeOriginZipCode}
                      onChange={(e, postalCodeValue) => {
                        if (postalCodeValue && postalCodeValue?.length > 0) {
                          setCountryProfileError({
                            ...countryProfileError,
                            administrativeOriginZipCode: "",
                          });
                        } else {
                          setCountryProfileError({
                            ...countryProfileError,
                            administrativeOriginZipCode: POSTALCODE_REQUIRED,
                          });
                        }
                        setCountryProfileInfo({
                          ...countryProfileInfo,
                          administrativeOriginZipCode: postalCodeValue || "",
                        });
                      }}
                      errorMessage={
                        countryProfileError.administrativeOriginZipCode
                      }
                    />
                  </StackItem>
                </Stack>
                <Stack verticalFill verticalAlign="start">
                  <StackItem verticalFill>
                    <ComboBox
                      data-testid="countryProfile-select"
                      styles={countryComboBoxStyles}
                      label="Country*"
                      autoComplete="on"
                      options={allCountries}
                      selectedKey={
                        countryProfileInfo.administrativeOriginCountry
                      }
                      onChange={(e, countryNameValue) => {
                        setCountryProfileInfo({
                          ...countryProfileInfo,
                          administrativeOriginCountry:
                            countryNameValue?.text?.toString() || "",
                        });
                        const statesData = countryWiseStateList.filter(
                          (item) => {
                            return (
                              item.country.toUpperCase() ===
                              countryNameValue?.text?.toUpperCase()
                            );
                          }
                        );
                        setFilteredOriginState(statesData);
                      }}
                      allowFreeInput
                      errorMessage={
                        countryProfileError.administrativeOriginCountry
                      }
                    />
                    <ComboBox
                      data-testid="countryProfile-select"
                      styles={countryComboBoxStyles}
                      label="State/Province"
                      autoComplete="on"
                      allowFreeInput
                      allowFreeform={true}
                      options={filteredOriginState}
                      selectedKey={countryProfileInfo.administrativeOriginState}
                      onChange={(e, stateValue) => {                      
                        setCountryProfileInfo({
                          ...countryProfileInfo,
                          administrativeOriginState:
                            stateValue?.key.toString() || "",
                        });
                      }}
                      errorMessage={
                        countryProfileError.administrativeOriginState
                      }
                    />
                    <TextField
                      styles={TextFieldStyles}
                      label="Street (Must not be P.O. Box)*"
                      name="street"
                      value={countryProfileInfo.administrativeOriginStreet}
                      onChange={(e, streetValue) => {
                        if (streetValue && streetValue?.length > 0) {
                          setCountryProfileError({
                            ...countryProfileError,
                            administrativeOriginStreet: "",
                          });
                        } else {
                          setCountryProfileError({
                            ...countryProfileError,
                            administrativeOriginStreet: STREET_REQUIRED,
                          });
                        }
                        setCountryProfileInfo({
                          ...countryProfileInfo,
                          administrativeOriginStreet: streetValue || "",
                        });
                      }}
                      errorMessage={
                        countryProfileError.administrativeOriginStreet
                      }
                    />
                  </StackItem>
                </Stack>
              </Stack>
            </Card>
          </Stack>
          <Stack
            horizontal
            style={{ paddingLeft: 20, width: 656, paddingTop: 10 }}
          >
            <Card
              header={
                <CardStandardHeader cardTitle="Fulfillment Warehouse Address" />
              }
            >
              <Stack horizontal horizontalAlign="space-between">
                <Stack verticalFill verticalAlign="start">
                  <StackItem verticalFill>
                    <ComboBox
                      data-testid="countryProfile-select"
                      styles={countryComboBoxStyles}
                      label="Country"
                      allowFreeform={true}
                      autoComplete="on"
                      options={allCountries}
                      selectedKey={countryProfileInfo.warehouseCountry}
                      onChange={(e, countryNameValue) => {
                        if (countryNameValue === undefined) {
                          setCountryProfileInfo({
                            ...countryProfileInfo,
                            warehouseState: "",
                          });
                        }
                        if (countryNameValue === undefined) {
                          setCountryProfileInfo({
                            ...countryProfileInfo,
                            warehouseCountry: "",
                            warehouseState: "",
                          });
                        } else {
                          setCountryProfileInfo({
                            ...countryProfileInfo,
                            warehouseCountry:
                              countryNameValue?.text.toString() || "",
                          });
                        }
                        const statesData = countryWiseStateList.filter(
                          (item) => {
                            return (
                              item.country.toUpperCase() ===
                              countryNameValue?.text?.toUpperCase()
                            );
                          }
                        );
                        setFilteredWarehouseState(statesData);
                      }}
                      allowFreeInput
                      errorMessage={countryProfileError.warehouseCountry}
                    />
                    <TextField
                      styles={TextFieldStyles}
                      label="City"
                      name="city"
                      value={countryProfileInfo.warehouseCity}
                      onChange={(e, cityValue) => {
                        if (cityValue && cityValue?.length > 0) {
                          setCountryProfileError({
                            ...countryProfileError,
                            warehouseCity: "",
                          });
                        }
                        setCountryProfileInfo({
                          ...countryProfileInfo,
                          warehouseCity: cityValue || "",
                        });
                      }}
                      errorMessage={countryProfileError.warehouseCity}
                    />
                    <TextField
                      styles={TextFieldStyles}
                      label="Zip/Postal Code"
                      name="postalCode"
                      value={countryProfileInfo.warehouseZipCode}
                      onChange={(e, postalCodeValue) => {
                        if (postalCodeValue && postalCodeValue.length > 0) {
                          setCountryProfileError({
                            ...countryProfileError,
                            warehouseZipCode: "",
                          });
                        }
                        setCountryProfileInfo({
                          ...countryProfileInfo,
                          warehouseZipCode: postalCodeValue || "",
                        });
                      }}
                      errorMessage={countryProfileError.warehouseZipCode}
                    />
                  </StackItem>
                </Stack>
                <Stack verticalFill verticalAlign="start">
                  <StackItem verticalFill>
                    <ComboBox
                      data-testid="countryProfile-select"
                      styles={countryComboBoxStyles}
                      label="State/Province"
                      autoComplete="on"
                      allowFreeform={true}
                      options={filteredWarehouseState}
                      selectedKey={countryProfileInfo.warehouseState}
                      onChange={(e, stateValue) => {
                        if (stateValue === undefined) {
                          setCountryProfileInfo({
                            ...countryProfileInfo,
                            warehouseState: "",
                          });
                        } else {
                          setCountryProfileInfo({
                            ...countryProfileInfo,
                            warehouseState: stateValue?.key.toString() || "",
                          });
                        }
                        if (
                          stateValue?.key &&
                          stateValue?.key.toString()?.length > 0
                        ) {
                          setCountryProfileError({
                            ...countryProfileError,
                            warehouseState: "",
                          });
                        }
                      }}
                      allowFreeInput
                      errorMessage={countryProfileError.warehouseState}
                    />
                    <TextField
                      styles={TextFieldStyles}
                      label="Street (Must not be P.O. Box)"
                      name="street"
                      value={countryProfileInfo.warehouseStreet}
                      onChange={(e, streetValue) => {
                        if (streetValue && streetValue.length > 0) {
                          setCountryProfileError({
                            ...countryProfileError,
                            warehouseStreet: "",
                          });
                        }
                        setCountryProfileInfo({
                          ...countryProfileInfo,
                          warehouseStreet: streetValue || "",
                        });
                      }}
                      errorMessage={countryProfileError.warehouseStreet}
                    />
                  </StackItem>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Stack>
      ) : (
        <Stack horizontal style={{ paddingLeft: 20, width: 656 }}>
          <Card
            styles={{ footer: { padding: "0" } }}
            header={<CardStandardHeader cardTitle="Office Details" />}
            footer={
              <Stack style={{ position: "relative" }}>
                <div
                  style={{
                    width: "100%",
                    height: "1px",
                    backgroundColor: "rgb(225, 223, 221)",
                    marginBottom: "10px",
                  }}
                ></div>
                {userRole === UserRoles.Admin && (
                  <CardFooterBase
                    styles={{ root: { marginLeft: "12px" } }}
                    links={[
                      {
                        onClick: () => {
                          setOfficeDetailsEdit(!officeDetailsEdit);
                        },
                        children: <>Edit details</>,
                        underline: false,
                      },
                    ]}
                  />
                )}
              </Stack>
            }
          >
            <Stack horizontal>
              <Stack
                verticalAlign="start"
                verticalFill
                style={{ width: "50%" }}
              >
                <StackItem>
                  <Stack>
                    <p style={pBoldStyle}>Microsoft Office details</p>
                    <p style={{ paddingLeft: '5px' }}>{countryProfileInfo?.administrativeOriginName}</p>
                    <p style={{ paddingLeft: "5px" }}>
                      {countryProfileInfo.administrativeOriginStreet &&
                        `${countryProfileInfo.administrativeOriginStreet} `}
                      {countryProfileInfo.administrativeOriginCity &&
                        `${countryProfileInfo.administrativeOriginCity} `}
                      {countryProfileInfo.administrativeOriginState &&
                        `${countryProfileInfo.administrativeOriginState} `}
                      {countryProfileInfo?.administrativeOriginCountry &&
                        `${countryProfileInfo?.administrativeOriginCountry} `}
                      {countryProfileInfo.administrativeOriginZipCode &&
                        `${countryProfileInfo.administrativeOriginZipCode}`}
                    </p>
                  </Stack>
                </StackItem>
              </Stack>
              <Stack
                verticalAlign="start"
                verticalFill
                style={{ width: "50%" }}
              >
                <StackItem>
                  <Stack>
                    <p style={pBoldStyle}>Fulfillment Warehouse Details</p>
                    <p style={{ paddingLeft: "5px" }}>
                      {countryProfileInfo.warehouseStreet &&
                        `${countryProfileInfo.warehouseStreet} `}
                      {countryProfileInfo.warehouseCity &&
                        `${countryProfileInfo.warehouseCity} `}
                      {countryProfileInfo.warehouseState &&
                        `${countryProfileInfo.warehouseState} `}
                      {countryProfileInfo.warehouseCountry &&
                        `${countryProfileInfo.warehouseCountry} `}
                      {countryProfileInfo.warehouseZipCode &&
                        `${countryProfileInfo.warehouseZipCode}`}
                    </p>
                  </Stack>
                </StackItem>
              </Stack>
            </Stack>
          </Card>
        </Stack>
      )}
            
      {/* Date Format Configuration */}
      <Stack horizontal style={{ paddingLeft: 20, width: 656, paddingTop: 10 }}>
        <DateFormatConfigCard 
          countryName={countryProfileInfo.countryName}
          isEditMode={dateFormatEdit}
          onEditToggle={() => setDateFormatEdit(!dateFormatEdit)} 
          initialDateFormat={countryProfileInfo.dateFormat}
          onDateFormatChange={(format) => {
            setCountryProfileInfo({
              ...countryProfileInfo,
              dateFormat: format
            });
          }}
        />
      </Stack>
      

      {remittanceAddressDetailsEdit ? (
        countryRemittanceDetails(countryProfileInfo.countryName)
      ) : (
        <Stack horizontal style={{ paddingLeft: 20, width: 656 }}>
          <Card
            styles={{ footer: { padding: "0" } }}
            header={
              <CardStandardHeader cardTitle="Remittance Address Details" />
            }
            footer={
              <Stack style={{ position: "relative" }}>
                <div
                  style={{
                    width: "100%",
                    height: "1px",
                    backgroundColor: "rgb(225, 223, 221)",
                    marginBottom: "10px",
                  }}
                ></div>
                {userRole === UserRoles.Admin && (
                  <CardFooterBase
                    styles={{ root: { marginLeft: "12px" } }}
                    links={[
                      {
                        onClick: () => {
                          setRemittanceAddressDetailsEdit(
                            !remittanceAddressDetailsEdit
                          );
                        },
                        children: <>Edit details</>,
                        underline: false,
                      },
                    ]}
                  />
                )}
              </Stack>
            }
          >
            <Stack>
              <table className={styles.border_none}>
                <tbody>
                  {countryProfileInfo?.bankName && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Bank Name</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.bankName}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.bankKey && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Bank Key</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.bankKey}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.bankPhone && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Bank Phone</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.bankPhone}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.bankAddress && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                      <strong>Bank Address</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.bankAddress}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.addressLine1 && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Address Line 1</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.addressLine1}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.addressLine2 && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Address Line 2</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.addressLine2}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.addressLine3 && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Address Line 3</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.addressLine3}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.accountName && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Account Name</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.accountName}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.accountAddress && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Account Address</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.accountAddress}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.accountNumber && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Account Number</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.accountNumber}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.iban && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>IBAN Number</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.iban}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.swiftCode && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Swift Code</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.swiftCode}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.switchCode && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Switch Code</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.switchCode}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.swiftBicCode && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Swift / BIC Code</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.swiftBicCode}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.vatId && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>VAT</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.vatId}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.bcCode && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>BC Code</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.bcCode}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.taxId && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Tax ID</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.taxId}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.sortCode && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Sort Code</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.sortCode}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.correspondingBank && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Corresponding Bank</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.correspondingBank}
                      </td>
                    </tr>
                  )}
                  {countryProfileInfo?.correspondingBankSwiftBICCode && (
                    <tr>
                      <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                        <strong>Corresponding Bank Swift / BIC Code</strong>
                      </td>
                      <td className={styles.border_none}>
                        {countryProfileInfo.correspondingBankSwiftBICCode}
                      </td>
                    </tr>
                  )}
                  {
                    countryProfileInfo?.remittanceCurrencyCode && (
                      <tr>
                        <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                          <strong>Currency Code</strong>
                        </td>
                        <td className={styles.border_none}>
                          {countryProfileInfo.remittanceCurrencyCode}
                        </td>
                      </tr>
                    )
                  }
                  {
                    countryProfileInfo?.remittanceCompanyCode && (
                      <tr>
                        <td className={`${styles.border_none} ${styles.text_noWrap}`}>
                          <strong>Company Code</strong>
                        </td>
                        <td className={styles.border_none}>
                          {countryProfileInfo.remittanceCompanyCode}
                        </td>
                      </tr>
                    )
                  }
                </tbody>
              </table>
            </Stack>
          </Card>
        </Stack>
      )}
      <Stack horizontal style={{ paddingLeft: 20, width: 656 }}>
        <Card
          header={
            <CardStandardHeader cardTitle="Terms and Conditions (Landscape only PDF)*" />
          }
          styles={{ root: { background: "#f2f2f2" } }}
        >
          <Stack verticalFill verticalAlign="start">
            {countryProfileInfo.termsAndConditions &&
              countryProfileInfo.termsAndConditions.map((item, index) => {
                return (
                  <Stack
                    horizontal
                    horizontalAlign="space-between"
                    style={{ alignItems: "center" }}
                  >
                    <Stack>
                      {
                        countryProfileInfo?.termsAndConditions[
                          countryProfileInfo?.termsAndConditions?.length -
                          index -
                          1
                        ].name
                      }
                      `
                      <br />
                      {countryProfileInfo?.termsAndConditions?.length > 0 &&
                        `${countryProfileInfo?.termsAndConditions[
                          countryProfileInfo?.termsAndConditions?.length -
                          index -
                          1
                        ].createdAt
                        }`}
                    </Stack>
                    {userRole === UserRoles.Admin && (
                      <Stack>
                        <PrimaryButton
                          text="Download"
                          type="button"
                          style={SubmitButtonStyle}
                          onClick={() =>
                            _OnClickHandleDownloadFile(
                              countryProfileInfo?.termsAndConditions?.length -
                              index -
                              1
                            )
                          }
                        />
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            {countryProfileInfo?.termsAndConditions?.length > 0 && (
              <Stack
                style={{
                  height: "1px",
                  width: "100%",
                  background: "#000000",
                  marginBottom: "15px",
                }}
              ></Stack>
            )}
            <Stack
              horizontal
              horizontalAlign="space-between"
              style={{ alignItems: "center" }}
            >
              <Stack>
                {termsAndCondiditionSelectedFile
                  ? `${termsAndCondiditionSelectedFile?.name}`
                  : `Upload the document to change the existing doc`}
              </Stack>
              {userRole === UserRoles.Admin && (
                <Stack>
                  <PrimaryButton
                    text="Upload"
                    type="button"
                    style={{ width: "110px" }}
                    iconProps={{ iconName: "Upload" }}
                    onClick={handleTermsAndConditionClick}
                  />
                  <input
                    type="file"
                    accept=".pdf"
                    aria-label="Upload terms and conditions PDF"
                    ref={termsAndConditionFileInputRef}
                    style={{ display: "none" }}
                    onChange={handleTermsAndConditionFileChange}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Card>
      </Stack>
      {officeDetailsEdit ||
        remittanceAddressDetailsEdit ||
        dateFormatEdit ||
        termsAndCondiditionSelectedFile ? (
        <Stack
          style={{ paddingLeft: 20, width: 656 }}
          horizontal
          horizontalAlign="center"
        >
          <PrimaryButton
            text="Save"
            type="button"
            style={SubmitButtonStyle}
            onClick={(e: any) => _OnClickSubmitButton(e)}
          />
        </Stack>
      ) : (
        userRole === UserRoles.Admin && (
          <Stack
            style={{ paddingLeft: 20, width: 656 }}
            horizontal
            horizontalAlign="center"
          >
            <PrimaryButton
              text={
                countryProfileInfo.isActive == "true" ? "Inactive" : "Active"
              }
              type="button"
              style={SubmitButtonStyle}
              onClick={(e: any) => _OnClickActiveOrInactive(e)}
            />
          </Stack>
        )
      )}
    </Stack>
  );
};
export default CountryProfileEditDetails;
